import { useState, useContext, useRef, useEffect, useCallback, type ChangeEvent } from 'react';
import { GridContext } from 'contexts/GridContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { ViewContext } from 'components/lib';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// import "leaflet-draw/dist/leaflet.draw-src.css";
import 'leaflet-draw/dist/leaflet.draw.css';
import 'proj4leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'react-leaflet-fullscreen/styles.css';
import Style from '../styles/grid.tailwind.js';
import { lv95ToWgs84ForOneCoordinate } from '../map/coordinateTransforms';
import { convertFromLv95ToUserCoordinate } from 'utils/coordinateSystemManager';
import OtherInfoForSite from '../OtherInfoForSite';
import Name from '../Name';
import useLocalCoordinateSystemSelector from 'hooks/useLocalCoordinateSystemSelector';
// turf.js
import * as turf from '@turf/turf';
import { areBorderCoordinatesEqual } from 'utils/geometryHelper.js';
import { cancelActiveDrawing } from 'lib/leafletHelper.js';
import CadastralMapSection from '../map/CadastralMapSection.js';
import CadastralBorderActions from './CadastralBorderActions.js';
import { useCadastralLayerRecovery } from './cadastralLayerRecovery';
import {
  type BorderType,
  type EditLayerHandlers,
  type Lv95Coordinate,
  type ManualRotateSession,
  type PolygonRef,
  type ShapeType,
  BUILDABLE_OFFSET_HINT,
  EDIT_DRAW_HINT,
  PARCEL_SELECTION_HINT,
  getPolygonRefByType,
} from './cadastralShared';
import type { RotateControlState } from './rotationControl';
import { getBuildableOffsetSourceBorder } from 'utils/buildableBorderHelper';
import Settings from 'settings.json';
import { crs2056, INITIAL_GRID_MAP_CENTER, INITIAL_GRID_MAP_VIEWS, type GridMapType } from '../map/mapConfig';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';
import { normalizeMultiPolygon, replacePolygonAtIndex } from 'utils/multiPolygonHelper';
import useAreaOffsetEditor from './useAreaOffsetEditor';
import AnalysisAreaTypeModal from './AnalysisAreaTypeModal';
import CadastralCoordinateEditor from './CadastralCoordinateEditor';
import AnalysisAreaCenterMarkerSyncContent, { useAnalysisAreaCenterMarker } from './AnalysisAreaCenterMarkerSync';
import useCadastralCoordinateInputs from './useCadastralCoordinateInputs';
import useCadastralRotation from './useCadastralRotation';
import createCadastralTransformHandlers from './createCadastralTransformHandlers';
import useCadastralBorderEditing from './useCadastralBorderEditing';
import createCadastralValidators from './createCadastralValidators';
import useCadastralDrawing from './useCadastralDrawing';
import createCadastralVolumeHandlers from './createCadastralVolumeHandlers';
import useCadastralParcelSelection from './useCadastralParcelSelection';
import './cadastralLeafletSetup';
import { MAX_ALLOWED_GRID_AREA, MAX_ANALYSIS_AREAS, MIN_ANALYSIS_AREA } from 'constants/grid';
import { buildTurfPolygonFromLv95, getCombinedAnalysisAreasCenter, isPolygonContained } from './cadastralGeometry';

interface CadastralProject {
  id?: string | number;
  coordinates?: { lat: number; lng: number } | null;
  [key: string]: unknown;
}

interface CadastralProps {
  project?: CadastralProject | null;
  projectId?: string | number;
  readOnly?: boolean;
  isReview?: boolean;
  defaultGridName?: string;
  onBillingLinkClick?: () => void;
}

const Cadastral = ({
  project,
  projectId,
  readOnly = false,
  isReview = false,
  defaultGridName = 'Grid 1',
  onBillingLinkClick,
}: CadastralProps) => {
  const readOnlyMode = Boolean(readOnly || isReview);
  // contexts
  const context = useContext(ViewContext);
  const {
    isGridCoordinateSystemValid,
    setIsGridAnalysisAreaValid,
    setGridAnalysisAreaErrorMessage,
    setIsGridCoordinateSystemValid,
  } = useContext(ValidationContext);

  const { gridParams, setGridParams, coordinateSystemPreference, setCoordinateSystemPreference } =
    useContext(GridContext);
  const { selectorValue, handleSelectorChange } = useLocalCoordinateSystemSelector(
    coordinateSystemPreference,
    setCoordinateSystemPreference
  );

  //states
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapViews, setMapViews] = useState(INITIAL_GRID_MAP_VIEWS);
  const [mapType, setMapType] = useState<GridMapType>('cadastral');
  const [showEditableCoordinatesArea, setShowEditableCoordinatesArea] = useState(false);
  // coordinates of the active polygon to be updated
  const [coordinatesOfTheUpdatedPolygon, setCoordinatesOfTheUpdatedPolygon] = useState<Lv95Coordinate[]>([]);
  const [editingPolygonType, setEditingPolygonType] = useState<BorderType | null>(null); //legalBorder or buildableBorder or analysisArea
  const [isLegalBorderParcelMode, setIsLegalBorderParcelMode] = useState(false);
  const [activeEditBorderType, setActiveEditBorderType] = useState<BorderType | null>(null);
  const [isDrawHintVisible, setIsDrawHintVisible] = useState(false);
  const [drawHintText, setDrawHintText] = useState('');

  const [isModalOpenForAnalysisAreaType, setIsModalOpenForAnalysisAreaType] = useState(false);
  const [analysisAreaType, setAnalysisAreaType] = useState(''); // asTheLegalBorder or asTheBuildableBorder
  const [activeAnalysisAreaIndex, setActiveAnalysisAreaIndex] = useState(0);

  // refs
  const currentTypeOfPolygonRef = useRef<BorderType | null>(null); //legalBorder or buildableBorder or analysisArea
  const currentShapeTypeRef = useRef<ShapeType | null>(null); // polygon or rectangle
  const isDrawingRef = useRef(false);
  const editKeydownHandlerRef = useRef<Partial<Record<BorderType, (event: KeyboardEvent) => void>>>({});
  const editLayerHandlersRef = useRef<Partial<Record<BorderType, EditLayerHandlers>>>({});
  const activeEditBorderRef = useRef<BorderType | null>(null);
  const editOriginalCoordinatesRef = useRef<Partial<Record<BorderType, Lv95Coordinate[] | null>>>({});
  const editDraftCoordinatesRef = useRef<Partial<Record<BorderType, Lv95Coordinate[] | null>>>({});
  const pendingDrawRef = useRef<{ shapeType: ShapeType | null; borderType: BorderType | null } | null>(null);
  const legalBorderRef = useRef<L.Polygon | null>(null);
  const buildableBorderRef = useRef<L.Polygon | null>(null);
  const analysisAreaRef = useRef<L.Polygon | null>(null);
  const analysisAreaInactiveRefs = useRef<L.Polygon[]>([]);
  const centerOfAnalysisAreaRef = useRef<L.Marker | null>(null);
  const reviewCenterMarkersRef = useRef<L.Marker[]>([]);
  const rotateControlRef = useRef<RotateControlState | null>(null);
  const rotateAngleDegreesRef = useRef(90);
  const manualRotateSessionRef = useRef<ManualRotateSession | null>(null);

  const { cadastralLayerKey, onCadastralTileLoadStart, onCadastralTileLoad, onCadastralTileError } =
    useCadastralLayerRecovery({
      mapType,
      setMapType,
      showNotification: context.notification.show,
    });
  // The recovery hook does not expose a layer-load callback; preserve the
  // previous behaviour of forwarding `undefined` to the map section.
  const onCadastralLayerLoad = undefined;

  const hasLegalBorder = gridParams.legalBorder.length > 0;
  const hasBuildableBorder = gridParams.buildableBorder.length > 0;
  const buildableOffsetSourceBorder = getBuildableOffsetSourceBorder(
    gridParams.legalBorder,
    gridParams.buildableBorder
  );
  const buildableOffsetSourceLabel = hasBuildableBorder ? 'buildable area' : 'property area';
  const legalBorders = normalizeMultiPolygon<Lv95Coordinate>(
    gridParams.legalBorders?.length ? gridParams.legalBorders : gridParams.legalBorder
  );
  const buildableBorders = normalizeMultiPolygon<Lv95Coordinate>(
    gridParams.buildableBorders?.length ? gridParams.buildableBorders : gridParams.buildableBorder
  );
  const analysisAreas = normalizeMultiPolygon<Lv95Coordinate>(
    gridParams.analysisAreas?.length ? gridParams.analysisAreas : gridParams.analysisArea
  );
  const isAnalysisAreaLinked =
    gridParams.analysisAreaType === 'asTheLegalBorder' || gridParams.analysisAreaType === 'asTheBuildableBorder';
  const analysisAreaLinkedLabel = gridParams.analysisAreaType === 'asTheLegalBorder' ? 'property' : 'buildable';

  const syncAnalysisAreas = useCallback(
    (
      nextAreas: Lv95Coordinate[][],
      options: {
        activeIndex?: number;
        analysisAreaType?: string;
        legalBorders?: Lv95Coordinate[][];
        buildableBorders?: Lv95Coordinate[][];
        manualCenter?: { x: number; y: number } | null;
        recenter?: boolean;
      } = {}
    ) => {
      const normalizedAreas = nextAreas
        .slice(0, MAX_ANALYSIS_AREAS)
        .map((polygon) => (Array.isArray(polygon) ? polygon : []));
      const normalizedLegalBorders = (options.legalBorders ?? legalBorders)
        .slice(0, MAX_ANALYSIS_AREAS)
        .map((polygon) => (Array.isArray(polygon) ? polygon : []));
      const normalizedBuildableBorders = (options.buildableBorders ?? buildableBorders)
        .slice(0, MAX_ANALYSIS_AREAS)
        .map((polygon) => (Array.isArray(polygon) ? polygon : []));
      const fallbackIndex = normalizedAreas.findIndex((polygon) => polygon.length > 0);
      const desiredIndex = options.activeIndex ?? activeAnalysisAreaIndex;
      const nextActiveIndex =
        normalizedAreas.length === 0
          ? 0
          : normalizedAreas[desiredIndex]?.length || desiredIndex < normalizedAreas.length
            ? desiredIndex
            : Math.max(fallbackIndex, 0);

      setActiveAnalysisAreaIndex(nextActiveIndex);
      const nextManualCenter =
        options.manualCenter !== undefined
          ? options.manualCenter
          : options.recenter
            ? getCombinedAnalysisAreasCenter(normalizedAreas)
            : undefined;
      setGridParams({
        legalBorders: normalizedLegalBorders,
        legalBorder: normalizedLegalBorders[nextActiveIndex] ?? [],
        buildableBorders: normalizedBuildableBorders,
        buildableBorder: normalizedBuildableBorders[nextActiveIndex] ?? [],
        analysisAreas: normalizedAreas,
        analysisArea: normalizedAreas[nextActiveIndex] ?? [],
        activeAnalysisAreaIndex: nextActiveIndex,
        ...(options.analysisAreaType !== undefined ? { analysisAreaType: options.analysisAreaType } : {}),
        ...(nextManualCenter !== undefined ? { manualCenter: nextManualCenter } : {}),
      });
    },
    [activeAnalysisAreaIndex, buildableBorders, legalBorders, setGridParams]
  );

  const replaceActiveAnalysisArea = useCallback(
    (polygon: Lv95Coordinate[]) => replacePolygonAtIndex(analysisAreas, activeAnalysisAreaIndex, polygon),
    [activeAnalysisAreaIndex, analysisAreas]
  );
  const replaceActiveLegalBorder = useCallback(
    (polygon: Lv95Coordinate[]) => replacePolygonAtIndex(legalBorders, activeAnalysisAreaIndex, polygon),
    [activeAnalysisAreaIndex, legalBorders]
  );
  const replaceActiveBuildableBorder = useCallback(
    (polygon: Lv95Coordinate[]) => replacePolygonAtIndex(buildableBorders, activeAnalysisAreaIndex, polygon),
    [activeAnalysisAreaIndex, buildableBorders]
  );
  const syncNonAnalysisBorder = useCallback(
    (borderType: Exclude<BorderType, 'analysisArea'>, coordinates: Lv95Coordinate[]) => {
      if (borderType === 'legalBorder') {
        syncAnalysisAreas(analysisAreas, {
          activeIndex: activeAnalysisAreaIndex,
          legalBorders: replaceActiveLegalBorder(coordinates),
        });
        return;
      }

      syncAnalysisAreas(analysisAreas, {
        activeIndex: activeAnalysisAreaIndex,
        buildableBorders: replaceActiveBuildableBorder(coordinates),
      });
    },
    [activeAnalysisAreaIndex, analysisAreas, replaceActiveBuildableBorder, replaceActiveLegalBorder, syncAnalysisAreas]
  );
  const syncActiveAnalysisAreaCoordinates = useCallback(
    (coordinates: Lv95Coordinate[], options: { analysisAreaType?: string; recenter?: boolean } = {}) => {
      syncAnalysisAreas(
        analysisAreas.map((polygon, index) => (index === activeAnalysisAreaIndex ? coordinates : polygon)),
        {
          activeIndex: activeAnalysisAreaIndex,
          ...(options.analysisAreaType !== undefined ? { analysisAreaType: options.analysisAreaType } : {}),
          ...(options.recenter !== undefined ? { recenter: options.recenter } : {}),
        }
      );
    },
    [activeAnalysisAreaIndex, analysisAreas, syncAnalysisAreas]
  );

  /**
   * Adjusts the center and zoom value of the map according to the address information from the project.
   */
  useEffect(() => {
    const nextCenter: [number, number] = project?.coordinates
      ? [project.coordinates.lat, project.coordinates.lng]
      : INITIAL_GRID_MAP_CENTER;
    setMapCenter(nextCenter);
    setMapViews((prev) => ({
      ...prev,
      cadastral: { ...prev.cadastral, center: nextCenter },
      osm: { ...prev.osm, center: nextCenter },
    }));
  }, [project]);

  const allVolumesHaveAnalysisArea = analysisAreas.length > 0 && analysisAreas.every((polygon) => polygon.length > 0);

  useEffect(() => {
    if (allVolumesHaveAnalysisArea) {
      setIsGridAnalysisAreaValid(true);
      setGridAnalysisAreaErrorMessage('');
    } else {
      setIsGridAnalysisAreaValid(false);
      setGridAnalysisAreaErrorMessage(
        analysisAreas.length > 1
          ? '*please define an analysis area for every volume'
          : '*please define an analysis area'
      );
    }
  }, [allVolumesHaveAnalysisArea, analysisAreas.length, setIsGridAnalysisAreaValid, setGridAnalysisAreaErrorMessage]);

  useEffect(() => {
    if (!analysisAreas.length) {
      if (
        activeAnalysisAreaIndex !== 0 ||
        gridParams.analysisArea.length > 0 ||
        gridParams.legalBorder.length > 0 ||
        gridParams.buildableBorder.length > 0
      ) {
        setActiveAnalysisAreaIndex(0);
        setGridParams({
          analysisArea: [],
          legalBorder: legalBorders[0] ?? [],
          buildableBorder: buildableBorders[0] ?? [],
          activeAnalysisAreaIndex: 0,
        });
      }
      return;
    }

    const nextIndex = Math.min(activeAnalysisAreaIndex, analysisAreas.length - 1);
    const nextArea = analysisAreas[nextIndex] ?? [];
    const nextLegalBorder = legalBorders[nextIndex] ?? [];
    const nextBuildableBorder = buildableBorders[nextIndex] ?? [];
    if (
      activeAnalysisAreaIndex !== nextIndex ||
      !areBorderCoordinatesEqual(gridParams.analysisArea, nextArea) ||
      !areBorderCoordinatesEqual(gridParams.legalBorder, nextLegalBorder) ||
      !areBorderCoordinatesEqual(gridParams.buildableBorder, nextBuildableBorder)
    ) {
      setActiveAnalysisAreaIndex(nextIndex);
      setGridParams({
        analysisArea: nextArea,
        legalBorder: nextLegalBorder,
        buildableBorder: nextBuildableBorder,
        activeAnalysisAreaIndex: nextIndex,
      });
    }
  }, [
    activeAnalysisAreaIndex,
    analysisAreas,
    buildableBorders,
    gridParams.analysisArea,
    gridParams.buildableBorder,
    gridParams.legalBorder,
    legalBorders,
    setGridParams,
  ]);

  useEffect(() => {
    if (gridParams.analysisAreaType === 'asTheLegalBorder') {
      if (!areBorderCoordinatesEqual(gridParams.analysisArea, gridParams.legalBorder)) {
        syncAnalysisAreas(replaceActiveAnalysisArea(gridParams.legalBorder), {
          activeIndex: activeAnalysisAreaIndex,
          analysisAreaType: 'asTheLegalBorder',
          recenter: true,
        });
      }
    } else if (gridParams.analysisAreaType === 'asTheBuildableBorder') {
      if (!areBorderCoordinatesEqual(gridParams.analysisArea, gridParams.buildableBorder)) {
        syncAnalysisAreas(replaceActiveAnalysisArea(gridParams.buildableBorder), {
          activeIndex: activeAnalysisAreaIndex,
          analysisAreaType: 'asTheBuildableBorder',
          recenter: true,
        });
      }
    }
  }, [
    gridParams.analysisAreaType,
    gridParams.analysisArea,
    gridParams.legalBorder,
    gridParams.buildableBorder,
    activeAnalysisAreaIndex,
    replaceActiveAnalysisArea,
    syncAnalysisAreas,
  ]);

  useEffect(() => {
    const fixedButtons = document.querySelectorAll('button[data-draw-shape], button[data-border-action]');
    fixedButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) {
        return;
      }
      const computedBackground = window.getComputedStyle(button).backgroundColor;
      if (computedBackground) {
        button.style.backgroundColor = computedBackground;
      }
    });
  }, [
    gridParams.legalBorder.length,
    legalBorders.length,
    gridParams.buildableBorder.length,
    buildableBorders.length,
    gridParams.analysisArea.length,
    analysisAreas.length,
    activeEditBorderType,
  ]);

  const { startDrawing, handleDrawRequest, onCreated, onDrawStart, onDrawStop } = useCadastralDrawing({
    mapType,
    activeEditBorderType,
    isLegalBorderParcelMode,
    setIsLegalBorderParcelMode,
    gridParams,
    analysisAreas,
    activeAnalysisAreaIndex,
    isDrawingRef,
    pendingDrawRef,
    currentTypeOfPolygonRef,
    currentShapeTypeRef,
    activeEditBorderRef,
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    setIsDrawHintVisible,
    setDrawHintText,
    setShowEditableCoordinatesArea,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
    setEditingPolygonType,
    setIsGridAnalysisAreaValid,
    setGridAnalysisAreaErrorMessage,
    cancelEditForBorder: (borderType) => cancelEditForBorder(borderType),
    interruptBuildableOffsetMode: () => handleBuildableOffsetModeInterruption(),
    interruptLegalBorderParcelMode: () => handleLegalBorderParcelModeInterruption(),
    validateLegalBuildableContainment: (borderType, polygon) => validateLegalBuildableContainment(borderType, polygon),
    validateProjectRadiusRequirement: (borderType, polygon) => validateProjectRadiusRequirement(borderType, polygon),
    validateAnalysisAreaSeparation: (borderType, polygon, options) =>
      validateAnalysisAreaSeparation(borderType, polygon, options),
    syncAnalysisAreas,
    replaceActiveLegalBorder,
    replaceActiveBuildableBorder,
    addCenterMarker: (...args) => addCenterMarker(...args),
    setGridParams,
    style: Style,
    notifyError: (message) => context.notification.show(message, 'error', true, 'toast'),
  });

  const {
    validateProjectRadiusRequirement,
    validateAnalysisAreaSeparation,
    validatePolygonDoesNotSelfIntersect,
    validateLegalBuildableContainment,
  } = createCadastralValidators({
    projectCoordinates: project?.coordinates,
    analysisAreas,
    gridParams,
    legalBorderRef,
    buildableBorderRef,
    notifyError: (message) => context.notification.show(message, 'error', true, 'toast'),
  });

  const applyDerivedBuildableBorder = (buildableBorder: Lv95Coordinate[]) => {
    const newPolygon = buildTurfPolygonFromLv95(buildableBorder);
    const legalPolygon = buildTurfPolygonFromLv95(gridParams.legalBorder);
    if (!newPolygon) {
      context.notification.show('Unable to derive the buildable area.', 'error', true, 'toast');
      return false;
    }

    const polygonArea = turf.area(newPolygon);
    const legalArea = legalPolygon ? turf.area(legalPolygon) : null;

    if (legalPolygon && !Number.isFinite(legalArea)) {
      context.notification.show('Unable to validate the resulting buildable area.', 'error', true, 'toast');
      return false;
    }

    if (legalPolygon && !isPolygonContained(legalPolygon, newPolygon)) {
      context.notification.show(
        'The selected offsets push part of the buildable area outside the property area.',
        'error',
        true,
        'toast'
      );
      return false;
    }

    if (polygonArea < MIN_ANALYSIS_AREA) {
      context.notification.show('The derived buildable area is too small.', 'error', true, 'toast');
      return false;
    }

    if (polygonArea > MAX_ALLOWED_GRID_AREA) {
      context.notification.show(
        'The derived buildable area exceeds the supported maximum area.',
        'error',
        true,
        'toast'
      );
      return false;
    }

    const selfIntersect = turf.kinks(newPolygon);
    if (selfIntersect.features.length !== 0) {
      context.notification.show('The derived buildable area intersects itself.', 'error', true, 'toast');
      return false;
    }

    syncAnalysisAreas(analysisAreas, {
      activeIndex: activeAnalysisAreaIndex,
      buildableBorders: replaceActiveBuildableBorder(buildableBorder),
    });
    setEditingPolygonType('buildableBorder');
    setCoordinatesOfTheUpdatedPolygon(buildableBorder);
    setShowEditableCoordinatesArea(true);
    context.notification.show('Buildable area configured correctly.', 'success', true, 'toast');
    return true;
  };

  const buildableOffsetEditor = useAreaOffsetEditor({
    area: buildableOffsetSourceBorder,
    areaLabel: buildableOffsetSourceLabel,
    introTitle: hasBuildableBorder ? 'Adjust buildable area offsets' : 'Derive buildable area from property area',
    inputName: 'buildableOffset',
    layerRef: hasBuildableBorder ? buildableBorderRef : legalBorderRef,
    onApply: applyDerivedBuildableBorder,
    onError: (message) => context.notification.show(message, 'error', true, 'toast'),
    onReset: () => {
      if (!activeEditBorderRef.current && !isLegalBorderParcelMode && !isDrawingRef.current) {
        setIsDrawHintVisible(false);
        setDrawHintText('');
      }
    },
    onStartSelecting: () => {
      setIsDrawHintVisible(true);
      setDrawHintText(BUILDABLE_OFFSET_HINT);
    },
  });
  const handleBuildableOffsetModeInterruption = buildableOffsetEditor.interrupt;

  const {
    selectedLegalBorderParcelFeatures,
    clearSelectedLegalBorderParcelFeatures,
    handleLegalBorderParcelSelected,
    handleSaveLegalBorderParcelSelection,
    handleLegalBorderParcelModeInterruption,
  } = useCadastralParcelSelection({
    isLegalBorderParcelMode,
    setIsLegalBorderParcelMode,
    activeAnalysisAreaIndex,
    analysisAreas,
    gridParams,
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    setIsDrawHintVisible,
    setDrawHintText,
    setShowEditableCoordinatesArea,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
    setEditingPolygonType,
    setIsGridAnalysisAreaValid,
    setGridAnalysisAreaErrorMessage,
    validateLegalBuildableContainment,
    validateProjectRadiusRequirement,
    validateAnalysisAreaSeparation,
    syncNonAnalysisBorder,
    syncAnalysisAreas,
    addCenterMarker: (...args) => addCenterMarker(...args),
    setGridParams,
    notify: (message, type) => context.notification.show(message, type, true, 'toast'),
  });

  const handleBeforeBorderAction = useCallback(() => {
    handleBuildableOffsetModeInterruption();
    handleLegalBorderParcelModeInterruption();
  }, [handleBuildableOffsetModeInterruption, handleLegalBorderParcelModeInterruption]);

  const handleOpenBuildableOffsetMode = () => {
    if (buildableOffsetSourceBorder.length < 3) {
      return;
    }

    if (activeEditBorderRef.current) {
      cancelEditForBorder(activeEditBorderRef.current);
    }

    cancelActiveDrawing({ isDrawingRef, pendingDrawRef, currentTypeOfPolygonRef, currentShapeTypeRef });
    handleLegalBorderParcelModeInterruption();
    buildableOffsetEditor.open();
  };

  const handleToggleBuildableOffsetMode = () => {
    if (buildableOffsetEditor.isEngaged) {
      handleBuildableOffsetModeInterruption();
      return;
    }
    handleOpenBuildableOffsetMode();
  };

  const handleToggleLegalBorderParcelMode = () => {
    handleBuildableOffsetModeInterruption();
    const nextMode = !isLegalBorderParcelMode;

    if (nextMode) {
      if (activeEditBorderRef.current) {
        cancelEditForBorder(activeEditBorderRef.current);
      }
      cancelActiveDrawing({ isDrawingRef, pendingDrawRef, currentTypeOfPolygonRef, currentShapeTypeRef });
      clearSelectedLegalBorderParcelFeatures();
      setIsDrawHintVisible(true);
      setDrawHintText(PARCEL_SELECTION_HINT);
    } else {
      clearSelectedLegalBorderParcelFeatures();
      setIsDrawHintVisible(false);
      setDrawHintText('');
    }

    setIsLegalBorderParcelMode(nextMode);
  };

  /**
   * Finds which type of polygon to change.
   */
  useEffect(() => {
    if (editingPolygonType) {
      let ref: PolygonRef | null = null;

      switch (editingPolygonType) {
        case 'legalBorder':
          ref = legalBorderRef;
          break;
        case 'buildableBorder':
          ref = buildableBorderRef;
          break;
        case 'analysisArea':
          ref = analysisAreaRef;
          break;
        default:
          break;
      }

      if (ref?.current) {
        const wgs84Coordinates = coordinatesOfTheUpdatedPolygon.map((coordinate) =>
          lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y)
        );

        ref.current.setLatLngs(wgs84Coordinates);
      }
    }
  }, [editingPolygonType, coordinatesOfTheUpdatedPolygon]);

  /**
   * Sets the coordinates of the polygon to be updated.
   */
  const editPolygon = (polygonType: BorderType) => {
    let polygonCoordinates: Lv95Coordinate[] = [];

    switch (polygonType) {
      case 'legalBorder':
        polygonCoordinates = gridParams?.legalBorder;
        break;
      case 'buildableBorder':
        polygonCoordinates = gridParams?.buildableBorder;
        break;
      case 'analysisArea':
        polygonCoordinates = gridParams?.analysisArea;
        break;
      default:
        break;
    }
    setCoordinatesOfTheUpdatedPolygon(polygonCoordinates);
    setEditingPolygonType(polygonType);
  };

  const { coordinateInputs, handleCoordinateInputChange } = useCadastralCoordinateInputs({
    coordinates: coordinatesOfTheUpdatedPolygon,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
    coordinateSystemPreference,
    editingPolygonType,
    editDraftCoordinatesRef,
  });

  /**
   * Opens the modal
   */
  const handleOpenModalForAnalysisAreaType = () => {
    handleBuildableOffsetModeInterruption();
    const preferredAnalysisAreaType =
      gridParams.analysisAreaType === 'asTheLegalBorder' && hasLegalBorder
        ? 'asTheLegalBorder'
        : gridParams.analysisAreaType === 'asTheBuildableBorder' && hasBuildableBorder
          ? 'asTheBuildableBorder'
          : hasLegalBorder
            ? 'asTheLegalBorder'
            : 'asTheBuildableBorder';

    setAnalysisAreaType(preferredAnalysisAreaType);
    setIsModalOpenForAnalysisAreaType(true);
  };

  /**
   * Closes the modal
   */
  const handleCloseModalForAnalysisAreaType = () => {
    setIsModalOpenForAnalysisAreaType(false);
  };

  /**
   * Changes the analysis area type
   */
  const handleChangeAnalysisAreaType = (event: ChangeEvent<HTMLInputElement>) => {
    setAnalysisAreaType(event.target.value);
  };

  /**
   * Sets the analysis area value according to the option selected from the Modal.
   */
  const checkAnalysisAreaType = () => {
    if (analysisAreaType === 'asTheLegalBorder') {
      const legalPolygon = buildTurfPolygonFromLv95(gridParams.legalBorder);
      if (!validateProjectRadiusRequirement('legalBorder', legalPolygon, 'asTheLegalBorder')) {
        return;
      }
      if (analysisAreaRef.current) {
        analysisAreaRef.current.remove();
        analysisAreaRef.current = null;
      }
      if (centerOfAnalysisAreaRef.current) {
        centerOfAnalysisAreaRef.current.remove();
        centerOfAnalysisAreaRef.current = null;
      }
      syncAnalysisAreas(replaceActiveAnalysisArea(gridParams.legalBorder), {
        activeIndex: activeAnalysisAreaIndex,
        analysisAreaType: 'asTheLegalBorder',
        recenter: true,
      });

      setIsGridAnalysisAreaValid(true);
      setGridAnalysisAreaErrorMessage('');

      // if (legalBorderRef.current && !analysisAreaRef.current) {
      //   const legalBorderLayer = legalBorderRef.current;
      //   const newLayer = L.polygon(
      //     legalBorderLayer.getLatLngs(),
      //     Style.analysisArea
      //   );
      //   newLayer.addTo(legalBorderLayer._map);
      //   analysisAreaRef.current = newLayer;
      // }
    }
    if (analysisAreaType === 'asTheBuildableBorder') {
      const buildablePolygon = buildTurfPolygonFromLv95(gridParams.buildableBorder);
      if (!validateProjectRadiusRequirement('buildableBorder', buildablePolygon, 'asTheBuildableBorder')) {
        return;
      }
      if (analysisAreaRef.current) {
        analysisAreaRef.current.remove();
        analysisAreaRef.current = null;
      }
      if (centerOfAnalysisAreaRef.current) {
        centerOfAnalysisAreaRef.current.remove();
        centerOfAnalysisAreaRef.current = null;
      }
      syncAnalysisAreas(replaceActiveAnalysisArea(gridParams.buildableBorder), {
        activeIndex: activeAnalysisAreaIndex,
        analysisAreaType: 'asTheBuildableBorder',
        recenter: true,
      });

      setIsGridAnalysisAreaValid(true);
      setGridAnalysisAreaErrorMessage('');

      // if (buildableBorderRef.current && !analysisAreaRef.current) {
      //   const buildableBorderLayer = buildableBorderRef.current;
      //   const newLayer = L.polygon(
      //     buildableBorderLayer.getLatLngs(),
      //     Style.analysisArea
      //   );
      //   newLayer.addTo(buildableBorderLayer._map);
      //   analysisAreaRef.current = newLayer;
      // }
    }
  };

  const {
    handleBorderDelete,
    handleDeleteActiveVolumeBorder,
    handleSelectAnalysisArea,
    handleAddAnalysisArea,
    handleDeleteActiveAnalysisArea,
    handleDeleteVolume,
  } = createCadastralVolumeHandlers({
    analysisAreas,
    legalBorders,
    buildableBorders,
    activeAnalysisAreaIndex,
    gridParams,
    activeEditBorderRef,
    editOriginalCoordinatesRef,
    editDraftCoordinatesRef,
    analysisAreaRef,
    centerOfAnalysisAreaRef,
    isDrawingRef,
    pendingDrawRef,
    currentTypeOfPolygonRef,
    currentShapeTypeRef,
    setShowEditableCoordinatesArea,
    setEditingPolygonType,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
    syncAnalysisAreas,
    replaceActiveAnalysisArea,
    replaceActiveLegalBorder,
    replaceActiveBuildableBorder,
    interruptBuildableOffsetMode: handleBuildableOffsetModeInterruption,
    interruptLegalBorderParcelMode: handleLegalBorderParcelModeInterruption,
    cancelEditForBorder: (borderType) => cancelEditForBorder(borderType),
    disableEditMode: (borderType) => disableEditMode(borderType),
  });

  const addCenterMarker = useAnalysisAreaCenterMarker(centerOfAnalysisAreaRef, () => {
    context.notification.show('Grid center updated!', 'success', true, 'toast');
  });

  const AnalysisAreaCenterMarkerSync = ({
    analysisArea,
    analysisAreas: _analysisAreas,
    manualCenter,
    mapType,
  }: {
    analysisArea: Lv95Coordinate[];
    analysisAreas?: Lv95Coordinate[][];
    manualCenter: Lv95Coordinate | null;
    mapType: GridMapType;
  }) => (
    <AnalysisAreaCenterMarkerSyncContent
      analysisArea={analysisArea}
      analysisAreas={analysisAreas}
      manualCenter={manualCenter}
      mapType={mapType}
      readOnlyMode={readOnlyMode}
      setGridParams={setGridParams}
      centerOfAnalysisAreaRef={centerOfAnalysisAreaRef}
      reviewCenterMarkersRef={reviewCenterMarkersRef}
      addCenterMarker={addCenterMarker}
    />
  );

  const {
    getLayerRefByType,
    getBorderCoordinatesByType,
    updateRotateControlPosition,
    removeRotateControl,
    renderRotateControl,
    ensureRotateControl,
  } = useCadastralRotation({
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    centerOfAnalysisAreaRef,
    rotateControlRef,
    rotateAngleDegreesRef,
    manualRotateSessionRef,
    activeEditBorderRef,
    editDraftCoordinatesRef,
    gridParams,
    analysisAreas,
    setGridParams,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
    addCenterMarker,
    onCenterReset: () => {
      context.notification.show('Grid center reset!', 'success', true, 'toast');
    },
  });

  const { enableRotateMode, enableDragMode } = createCadastralTransformHandlers({
    legalBorderRef,
    buildableBorderRef,
    analysisAreaRef,
    activeAnalysisAreaIndex,
    analysisAreas,
    gridParams,
    getBorderCoordinatesByType,
    validateProjectRadiusRequirement,
    validateAnalysisAreaSeparation,
    syncActiveAnalysisAreaCoordinates,
    syncNonAnalysisBorder,
    setCoordinates: setCoordinatesOfTheUpdatedPolygon,
  });

  const { snapshotEditState, disableEditMode, saveEditForBorder, cancelEditForBorder, enableEditMode } =
    useCadastralBorderEditing({
      activeEditBorderType,
      setActiveEditBorderType,
      mapType,
      gridParams,
      analysisAreas,
      activeAnalysisAreaIndex,
      coordinates: coordinatesOfTheUpdatedPolygon,
      setCoordinates: setCoordinatesOfTheUpdatedPolygon,
      setEditingPolygonType,
      setShowEditableCoordinatesArea,
      activeEditBorderRef,
      editOriginalCoordinatesRef,
      editDraftCoordinatesRef,
      editKeydownHandlerRef,
      editLayerHandlersRef,
      getLayerRefByType,
      getBorderCoordinatesByType,
      removeRotateControl,
      renderRotateControl,
      updateRotateControlPosition,
      ensureRotateControl,
      validatePolygonDoesNotSelfIntersect,
      validateLegalBuildableContainment,
      validateProjectRadiusRequirement,
      validateAnalysisAreaSeparation,
      syncActiveAnalysisAreaCoordinates,
      syncNonAnalysisBorder,
      addCenterMarker,
      setGridParams,
      onOutsideSwitzerland: () => {
        context.notification.show('Every polygon point must be inside Switzerland.', 'error', true, 'toast');
      },
    });

  const handleEditButtonClick = (borderType: BorderType) => {
    handleBuildableOffsetModeInterruption();
    handleLegalBorderParcelModeInterruption();
    setIsDrawHintVisible(false);
    setDrawHintText('');
    cancelActiveDrawing({ isDrawingRef, pendingDrawRef, currentTypeOfPolygonRef, currentShapeTypeRef });
    const previousBorderType = activeEditBorderRef.current;
    const switchingFromOther = previousBorderType != null && previousBorderType !== borderType;
    if (switchingFromOther) {
      cancelEditForBorder(previousBorderType);
    }

    if (activeEditBorderType === borderType) {
      saveEditForBorder(borderType);
      return;
    }

    const startEdit = () => {
      snapshotEditState(borderType);
      editPolygon(borderType);
      setShowEditableCoordinatesArea(true);
      enableEditMode(borderType);
    };

    if (switchingFromOther) {
      setTimeout(startEdit, 0);
    } else {
      startEdit();
    }
  };

  return (
    <div className="grid-analysis-step">
      <Name
        projectId={
          projectId !== undefined ? String(projectId) : project?.id !== undefined ? String(project.id) : undefined
        }
        isReviewMode={readOnlyMode}
        defaultGridName={defaultGridName}
      />
      <ProjectCreationSplitLayout
        className="grid-settings-layout"
        leftClassName="grid-settings-step__sidebar"
        rightClassName="grid-settings-layout__map"
        left={
          <>
            <CadastralBorderActions
              isReview={readOnlyMode}
              gridParams={gridParams}
              setGridParams={setGridParams}
              startDrawing={startDrawing}
              handleDrawRequest={handleDrawRequest}
              handleEditButtonClick={handleEditButtonClick}
              legalBorderRef={legalBorderRef}
              buildableBorderRef={buildableBorderRef}
              analysisAreaRef={analysisAreaRef}
              setShowEditableCoordinatesArea={setShowEditableCoordinatesArea}
              enableRotateMode={enableRotateMode}
              enableDragMode={enableDragMode}
              activeEditBorderType={activeEditBorderType}
              handleLinkedAnalysisAreaDelete={handleBorderDelete}
              handleOpenModalForAnalysisAreaType={handleOpenModalForAnalysisAreaType}
              centerOfAnalysisAreaRef={centerOfAnalysisAreaRef}
              isAnalysisAreaLinked={isAnalysisAreaLinked}
              isGridCoordinateSystemValid={isGridCoordinateSystemValid}
              analysisAreaLinkedLabel={analysisAreaLinkedLabel}
              hasLinkedBorderOptions={hasLegalBorder || hasBuildableBorder}
              isLegalBorderParcelMode={isLegalBorderParcelMode}
              onToggleLegalBorderParcelMode={handleToggleLegalBorderParcelMode}
              legalBorderParcelSelectionCount={selectedLegalBorderParcelFeatures.length}
              onSaveLegalBorderParcelSelection={handleSaveLegalBorderParcelSelection}
              isLegalBorderParcelModeUnavailable={mapType === 'osm'}
              isBuildableOffsetModeActive={buildableOffsetEditor.isActive}
              onToggleBuildableOffsetMode={handleToggleBuildableOffsetMode}
              onBeforeBorderAction={handleBeforeBorderAction}
              analysisAreaCount={analysisAreas.length}
              activeAnalysisAreaIndex={activeAnalysisAreaIndex}
              maxAnalysisAreas={MAX_ANALYSIS_AREAS}
              allVolumesHaveAnalysisArea={allVolumesHaveAnalysisArea}
              onSelectPreviousAnalysisArea={() => handleSelectAnalysisArea(activeAnalysisAreaIndex - 1)}
              onSelectNextAnalysisArea={() => handleSelectAnalysisArea(activeAnalysisAreaIndex + 1)}
              onAddAnalysisArea={handleAddAnalysisArea}
              onDeleteActiveAnalysisArea={handleDeleteActiveAnalysisArea}
              onDeleteVolume={handleDeleteVolume}
              onDeleteActiveLegalBorder={() => handleDeleteActiveVolumeBorder('legalBorder')}
              onDeleteActiveBuildableBorder={() => handleDeleteActiveVolumeBorder('buildableBorder')}
              renderCoordinateEditor={(borderType) =>
                showEditableCoordinatesArea && activeEditBorderType === borderType ? (
                  <CadastralCoordinateEditor
                    borderType={editingPolygonType}
                    coordinates={coordinatesOfTheUpdatedPolygon}
                    coordinateInputs={coordinateInputs}
                    disabled={readOnlyMode}
                    selectorValue={selectorValue}
                    onSelectorChange={handleSelectorChange}
                    onValidationChange={setIsGridCoordinateSystemValid}
                    onCoordinateChange={handleCoordinateInputChange}
                    toDisplayCoordinate={(coordinate) =>
                      convertFromLv95ToUserCoordinate(coordinate, coordinateSystemPreference)
                    }
                  />
                ) : null
              }
            />

            <div className="grid-settings-layout__details">
              <OtherInfoForSite
                showHeightInput
                showVolumeSummary
                showSensorPointsSummary={false}
                readOnly={readOnlyMode}
                onBillingLinkClick={onBillingLinkClick}
              />
            </div>
          </>
        }
        right={
          <CadastralMapSection
            isReview={readOnlyMode}
            readOnly={readOnlyMode}
            showProjectLocationCircle
            isParcelInteractionDisabled={!isLegalBorderParcelMode || Boolean(activeEditBorderType)}
            mapType={mapType}
            mapViews={mapViews}
            setMapType={setMapType}
            setMapViews={setMapViews}
            mapCenter={mapCenter}
            crs2056={crs2056}
            gridParams={gridParams}
            Style={Style}
            legalBorderRef={legalBorderRef}
            buildableBorderRef={buildableBorderRef}
            analysisAreaRef={analysisAreaRef}
            analysisAreaInactiveRefs={analysisAreaInactiveRefs}
            inactiveAnalysisAreaStyle={readOnlyMode ? Style.analysisArea : undefined}
            activeAnalysisAreaIndex={activeAnalysisAreaIndex}
            showInactiveAnalysisAreas={!readOnlyMode}
            isDrawingRef={isDrawingRef}
            showEditableCoordinatesArea={showEditableCoordinatesArea}
            AnalysisAreaCenterMarkerSync={AnalysisAreaCenterMarkerSync}
            onCreated={onCreated}
            onDrawStart={onDrawStart}
            onDrawStop={onDrawStop}
            cadastralOpacity={Number(Settings[import.meta.env.MODE as keyof typeof Settings].cadastral_opacity)}
            focusedPolygonRef={getPolygonRefByType(
              editingPolygonType,
              legalBorderRef,
              buildableBorderRef,
              analysisAreaRef
            )}
            coordinateSystem={coordinateSystemPreference}
            cadastralLayerKey={cadastralLayerKey}
            onCadastralTileLoadStart={onCadastralTileLoadStart}
            onCadastralTileLoad={onCadastralTileLoad}
            onCadastralLayerLoad={onCadastralLayerLoad}
            onCadastralTileError={onCadastralTileError}
            onBeforeMapAction={handleLegalBorderParcelModeInterruption}
            selectedParcelFeatures={selectedLegalBorderParcelFeatures}
            onCadastralParcelSelected={(feature) =>
              handleLegalBorderParcelSelected(feature as unknown as GeoJSON.Feature)
            }
            onCadastralParcelMissClick={handleLegalBorderParcelModeInterruption}
          />
        }
        rightFooter={
          <div className="grid-map-hint-slot" aria-live="polite">
            <p className={`grid-map-hint ${activeEditBorderType || isDrawHintVisible ? 'grid-map-hint--visible' : ''}`}>
              {activeEditBorderType ? EDIT_DRAW_HINT : drawHintText || ' '}
            </p>
          </div>
        }
      />

      <AnalysisAreaTypeModal
        open={isModalOpenForAnalysisAreaType}
        value={analysisAreaType}
        hasLegalBorder={hasLegalBorder}
        hasBuildableBorder={hasBuildableBorder}
        modalStyle={Style.modal}
        onClose={handleCloseModalForAnalysisAreaType}
        onChange={handleChangeAnalysisAreaType}
        onConfirm={() => {
          handleCloseModalForAnalysisAreaType();
          checkAnalysisAreaType();
        }}
      />

      {buildableOffsetEditor.dialogs}
    </div>
  );
};

export default Cadastral;
